/**
 * WorkspacePageV2 — Full V2 canvas workspace
 *
 * Sprint 8: Wires together ReactFlow canvas with canvas-v2 node/edge types,
 * Yjs real-time sync, CanvasContext (all methods delegating to Y doc + backend API),
 * undo/redo (50-entry in-memory stack), copy/paste/duplicate with edge remapping,
 * keyboard shortcuts (Cmd+C/V/D/Z, Shift+Z), viewport management (Y doc persistence),
 * CanvasToolbox + CanvasControls + InteractiveMiniMap, and config sync (500ms debounce).
 *
 * Route: /v2/project/:projectId/workspace/:canvasId?
 */

import { useCallback, useEffect, useState, useRef, useMemo } from 'react'
import { useParams, useSearchParams, useLocation } from 'react-router-dom'
import { ProjectIdProvider } from '../contexts/ProjectIdContext'
import ReactFlow, {
  Background,
  BackgroundVariant,
  ReactFlowProvider,
  Panel,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge as rfAddEdge,
  useReactFlow,
  PanOnScrollMode,
  type Connection,
  type Edge,
  type Node,
  type NodeSelectionChange,
} from 'reactflow'
import 'reactflow/dist/style.css'

import {
  nodeTypes,
  edgeTypes,
  NodeHoverContext,
  useNodeHoverState,
  CanvasContext,
  type CanvasContextValue,
  CanvasToolbox,
  createNode,
  nodeSchemas,
  areTypesCompatible,
} from '../components/canvas-v2'

import { useYDoc } from '../hooks/useYDoc'
import { useYCanvas, useYViewport } from '../hooks/useYCanvas'
import { useProject } from '../api/hooks'
import {
  executeNode,
  addNodesToGraph,
  addEdgesToGraph,
  removeNodesFromGraph,
  removeEdgesFromGraph,
} from '../api/client'
import { CanvasControls, LoadingState, ErrorState } from '../components/workspace/CanvasArea'
import { ChatSidebar } from '../components/workspace/ChatSidebar'
import UsageCostModal from '../components/ui/UsageCostModal'
import { useLocalStorage } from '../hooks/useLocalStorage'
import { PublishButton } from '../components/workspace/PublishButton'
import SparksBalance from '../components/layout/SparksBalance'
import { GroupDropTargetProvider } from '../components/canvas-v2/nodes/GroupNode/GroupDropTargetContext'
import { useCanvasDragToGroup } from '../components/canvas-v2/nodes/GroupNode/useCanvasDragToGroup'
import { createDebug } from '../utils/debugLog'

const dragLog = createDebug('dw:panel-drag:position-sync')
const dimSyncLog = createDebug('dw:layout:dimension-sync')

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_CANVAS_ID = 'agent'
const MAX_UNDO = 50
const PASTE_OFFSET = 50

/**
 * Frontend-to-backend type mapping for V2 nodes.
 * Only node types present here are synced to the backend graph.
 */
const FRONTEND_TO_BACKEND_TYPE: Record<string, string> = {
  characterDesigner: 'character_designer',
  locationDesigner: 'location_designer',
  objectDesigner: 'object_designer',
  scriptWriter: 'script_writer',
  videoGenerator: 'video_generator',
  videoEditor: 'video_editor',
  musicGenerator: 'music_generator',
  speechGenerator: 'speech_generator',
  imageGenerator: 'image_generator',
  soundExpert: 'sound_expert',
  nodeGroup: 'node_group',
  image: 'image',
  video: 'video',
  audio: 'audio',
}

function shouldSyncToBackend(type: string): boolean {
  return type in FRONTEND_TO_BACKEND_TYPE
}

// ─── ReactFlow Instance Capture ───────────────────────────────────────────────

function ReactFlowCapture({
  instanceRef,
  onReady,
}: {
  instanceRef: React.MutableRefObject<ReturnType<typeof useReactFlow> | null>
  onReady?: () => void
}) {
  const instance = useReactFlow()
  useEffect(() => {
    instanceRef.current = instance
    onReady?.()
  }, [instance, instanceRef, onReady])
  return null
}

// ─── InteractiveMiniMap ───────────────────────────────────────────────────────

function InteractiveMiniMap() {
  const rf = useReactFlow()
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const svg = container.querySelector('svg')
    if (!svg) return

    let pressed = false

    function panTo(e: PointerEvent) {
      const rect = svg!.getBoundingClientRect()
      const vb = svg!.viewBox.baseVal
      const x = vb.x + ((e.clientX - rect.left) / rect.width) * vb.width
      const y = vb.y + ((e.clientY - rect.top) / rect.height) * vb.height
      rf.setCenter(x, y, { zoom: rf.getZoom(), duration: 0 })
    }

    const onDown = (e: PointerEvent) => { pressed = true; panTo(e) }
    const onMove = (e: PointerEvent) => { if (pressed) panTo(e) }
    const onUp = () => { pressed = false }

    svg.addEventListener('pointerdown', onDown)
    svg.addEventListener('pointermove', onMove)
    svg.addEventListener('pointerup', onUp)
    window.addEventListener('pointerup', onUp)

    return () => {
      svg.removeEventListener('pointerdown', onDown)
      svg.removeEventListener('pointermove', onMove)
      svg.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointerup', onUp)
    }
  }, [rf])

  return (
    <div ref={containerRef}>
      <MiniMap
        className="!bg-card !border !border-border !rounded-lg !shadow-sm overflow-hidden"
        nodeColor={(node) => {
          const status = (node.data as { status?: string })?.status
          if (status === 'running') return '#22c55e'
          if (status === 'error') return '#ef4444'
          if (status === 'success') return '#3b82f6'
          return '#6b7280'
        }}
        maskColor="rgba(0, 0, 0, 0.5)"
        style={{ width: 160, height: 100 }}
      />
    </div>
  )
}

// ─── WorkspaceV2Inner ─────────────────────────────────────────────────────────

function WorkspaceV2Inner() {
  const { projectId: routeProjectId } = useParams<{ projectId?: string }>()
  const projectId = routeProjectId
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const activeCanvasId = searchParams.get('canvas') || DEFAULT_CANVAS_ID

  // Read initial prompt, style, and production target from navigation state
  const locationState = location.state as {
    initialPrompt?: string
    style?: string
    productionTarget?: string
  } | null
  const initialPrompt = locationState?.initialPrompt
  const initialStyle = locationState?.style
  const initialProductionTarget = locationState?.productionTarget

  // Project data
  const { data: project, isLoading: projectLoading } = useProject(projectId || '')

  // Agent conversation initialization
  const [conversationLoading, setConversationLoading] = useState(false)

  useEffect(() => {
    if (!projectId) return
    const initConversation = async () => {
      setConversationLoading(true)
      try {
        const { getOrCreateAgentConversation } = await import('../api/client')
        await getOrCreateAgentConversation(projectId)
      } catch (error) {
        console.error('[WorkspaceV2] Failed to load agent conversation:', error)
      } finally {
        setConversationLoading(false)
      }
    }
    initConversation()
  }, [projectId])

  const isLoading = projectLoading || conversationLoading

  // Director settings (persisted to localStorage)
  const [skipCostConfirmation, setSkipCostConfirmation] = useLocalStorage(
    'dreamwright_skip_cost_confirmation',
    true
  )

  // Usage & cost modal (RFC-0103)
  const [showUsageCost, setShowUsageCost] = useState(false)

  // Canvas state
  const nodesRef = useRef<Node[]>([])
  const edgesRef = useRef<Edge[]>([])
  const [nodes, setNodes, onNodesChangeBase] = useNodesState<Node[]>([])
  const [edges, setEdges, onEdgesChangeBase] = useEdgesState([])

  // Keep refs in sync with state
  useEffect(() => { nodesRef.current = nodes }, [nodes])
  useEffect(() => { edgesRef.current = edges }, [edges])

  // ─── Scalar selection (single source of truth) ──────────────────────
  // Selection is lifted out of the distributed node.selected booleans
  // into a single scalar. The ref provides synchronous reads (inside
  // onNodesChange, applySelection); the state triggers re-renders and
  // the stamping useEffect.
  const selectedNodeIdRef = useRef<string | null>(null)
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)

  // Stamp node.selected from the scalar whenever it changes.
  // For user clicks this is a backup — onNodesChange stamps synchronously.
  // For programmatic changes (openPanelToTab, duplicateNode) this ensures
  // ReactFlow's visual selection stays in sync.
  useEffect(() => {
    setNodes(prev => prev.map(n => ({
      ...n,
      selected: n.id === selectedNodeId,
    })))
  }, [selectedNodeId, setNodes])

  // Handle stop: set all running nodes to idle
  const handleStop = useCallback(() => {
    setNodes(nds => nds.map(n => {
      const status = (n.data as { status?: string })?.status
      if (status === 'running') {
        return { ...n, data: { ...n.data, status: 'idle' } }
      }
      return n
    }))
  }, [setNodes])

  // Pan & zoom the canvas to center a specific node (used by mention pill clicks)
  const handleMentionClick = useCallback((nodeId: string) => {
    const rf = reactFlowInstance.current
    if (!rf) return
    const target = nodes.find(n => n.id === nodeId)
    if (!target) return
    rf.fitView({ nodes: [target], padding: 0.3, duration: 500, maxZoom: 1 })
  }, [nodes])

  // Stable node list for ChatSidebar @mention autocomplete — only updates when
  // node IDs or labels change, not on every position change during drag.
  const nodeIdentityKey = useMemo(
    () => nodes.map(n => {
      const d = n.data as unknown as Record<string, unknown> | undefined
      const label = d?.name ?? d?.character_name ?? d?.location_name ?? d?.in_name ?? d?.label ?? ''
      return `${n.id}:${label}`
    }).join(','),
    [nodes],
  )
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const canvasNodesStable = useMemo(() => nodes, [nodeIdentityKey])

  // ReactFlow instance
  const reactFlowInstance = useRef<ReturnType<typeof useReactFlow> | null>(null)
  const [rfInstanceReady, setRfInstanceReady] = useState(false)
  const onRfInstanceReady = useCallback(() => setRfInstanceReady(true), [])

  // ─── Yjs Integration ─────────────────────────────────────────────────────

  const { ydoc, synced: yjsSynced, connected: yjsConnected } = useYDoc({
    projectId: projectId || '',
    canvasId: activeCanvasId,
    enabled: !!projectId,
  })
  const isBackendConnected = yjsConnected && yjsSynced

  // Undo/Redo state (declared before useYCanvas since it needs isUndoRedo)
  const undoStack = useRef<Array<{ nodes: Node[]; edges: Edge[] }>>([])
  const redoStack = useRef<Array<{ nodes: Node[]; edges: Edge[] }>>([])
  const isUndoRedo = useRef(false)

  const {
    ready: canvasReady,
    ydocRunningCount,
    ydocQueuedCount,
    writeNodePosition,
    addNodeToYDoc,
    writeNodeData,
    writeNodeDimensions,
    writeNodeParent,
  } = useYCanvas({
    ydoc,
    synced: yjsSynced,
    setNodes,
    setEdges,
    nodesRef,
    edgesRef,
    isBackendConnected,
    isUndoRedo,
    reactFlowInstance,
    selectedNodeIdRef,
  })
  const { viewport: yjsViewport, writeViewport } = useYViewport(ydoc, yjsSynced)

  // ─── Viewport Management ─────────────────────────────────────────────────

  const [canvasVisible, setCanvasVisible] = useState(false)
  const yjsViewportRestoredRef = useRef(false)

  // Reset viewport guard on sync session change
  useEffect(() => {
    if (!yjsSynced) {
      yjsViewportRestoredRef.current = false
    }
  }, [yjsSynced])

  // Restore viewport from Y doc on initial sync
  useEffect(() => {
    if (
      yjsViewport &&
      yjsSynced &&
      !yjsViewportRestoredRef.current &&
      reactFlowInstance.current
    ) {
      const isDefault =
        yjsViewport.x === 0 && yjsViewport.y === 0 && yjsViewport.zoom === 1
      if (!isDefault) {
        reactFlowInstance.current.setViewport(yjsViewport, { duration: 0 })
      }
      yjsViewportRestoredRef.current = true
      setCanvasVisible(true)
    }
  }, [yjsSynced, yjsViewport, rfInstanceReady])

  // Show canvas once canvasReady fires (even if no saved viewport)
  useEffect(() => {
    if (canvasReady && !canvasVisible) {
      // If viewport wasn't restored from Y doc, apply fitView
      if (!yjsViewportRestoredRef.current && reactFlowInstance.current && nodes.length > 0) {
        reactFlowInstance.current.fitView({ padding: 0.2, maxZoom: 1, duration: 0 })
        yjsViewportRestoredRef.current = true
      }
      setCanvasVisible(true)
    }
  }, [canvasReady, canvasVisible, nodes.length, rfInstanceReady])

  // Persist viewport on move end
  const onMoveEndSave = useCallback(() => {
    if (reactFlowInstance.current) {
      writeViewport(reactFlowInstance.current.getViewport())
    }
  }, [writeViewport])

  // Save viewport on page unload (catches cases where onMoveEnd didn't fire)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (reactFlowInstance.current) {
        writeViewport(reactFlowInstance.current.getViewport())
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [writeViewport])

  // Node hover state
  const nodeHoverState = useNodeHoverState()

  // ─── Node Execution ───────────────────────────────────────────────────────

  const handleExecuteNode = useCallback(
    async (node: Node) => {
      if (node.data?.status === 'running') return
      if (!projectId) return

      // Track active execution
      const activeNodes = (
        window as unknown as { __activeExecutingNodes?: Set<string> }
      ).__activeExecutingNodes ??= new Set<string>()
      activeNodes.add(node.id)

      // Set running state
      setNodes((nds) =>
        nds.map((n) =>
          n.id === node.id
            ? { ...n, data: { ...n.data, status: 'running', error: undefined } }
            : n
        )
      )
      writeNodeData(node.id, { status: 'running', error: undefined })

      // Execute via REST (fire-and-forget — backend reads data from YDoc snapshot)
      await executeNode(projectId, node.id)
    },
    [projectId, setNodes, writeNodeData]
  )

  // ─── CanvasContext Value ──────────────────────────────────────────────────

  const canvasContextValue: CanvasContextValue = useMemo(
    () => ({
      addNode: (
        type: string,
        position: { x: number; y: number },
        data?: Record<string, unknown>,
        options?: { autoExecute?: boolean }
      ) => {
        const newNode = createNode(type as keyof typeof nodeSchemas, position, {
          ...data,
          projectId,
          cacheKey: project?.updated_at,
        })
        const typedNewNode = newNode as unknown as Node

        setNodes((nds) => {
          const updated = [...nds, typedNewNode]
          nodesRef.current = updated
          return updated
        })

        // Pre-create in Y doc
        if (shouldSyncToBackend(type)) {
          addNodeToYDoc(
            newNode.id,
            type,
            (data?.label as string) ?? (data?.name as string) ?? newNode.id,
            position.x,
            position.y,
            data,
            true
          )
        }

        // Group nodes are frontend-only (not synced to backend) but still need
        // a Y doc entry so that writeNodeData (used by updateNodeData) can find
        // the node and persist child_node_ids updates.
        if (type === 'group') {
          addNodeToYDoc(
            newNode.id,
            type,
            (data?.label as string) ?? 'Group',
            position.x,
            position.y,
            data,
          )
        }

        // Sync to backend
        if (projectId && shouldSyncToBackend(type)) {
          const backendType = FRONTEND_TO_BACKEND_TYPE[type]
          addNodesToGraph(projectId, [
            { id: newNode.id, type: backendType, config: data ?? {} },
          ])
            .then(() => {
              setNodes((nds) =>
                nds.map((n) =>
                  n.id === newNode.id
                    ? { ...n, data: { ...n.data, syncedToBackend: true } }
                    : n
                )
              )
            })
            .catch((error) => {
              console.error('[WorkspaceV2:addNode] Backend sync failed:', error)
            })
        }

        // Auto-execute if requested
        if (options?.autoExecute) {
          const schema = nodeSchemas[type as keyof typeof nodeSchemas]
          const hasBackendExecutor = !!(schema && 'backendNodeId' in schema)
          if (hasBackendExecutor) {
            setTimeout(() => handleExecuteNode(typedNewNode), 100)
          }
        }

        return newNode.id
      },

      addEdge: (source: string, sourceHandle: string, target: string, targetHandle: string) => {
        setEdges((eds) => {
          const newEdge: Edge = {
            id: `${source}-${target}-${Date.now()}`,
            source,
            sourceHandle,
            target,
            targetHandle,
          }
          return [...eds, newEdge]
        })
      },

      removeNode: (nodeId: string) => {
        // Clean up parent group's in_child_node_ids (same logic as onNodesChange removal handler)
        const removedNode = nodesRef.current.find((n) => n.id === nodeId)
        if (removedNode?.parentNode) {
          const parentNode = nodesRef.current.find((n) => n.id === removedNode.parentNode)
          if (parentNode?.type === 'nodeGroup') {
            const parentData = parentNode.data as { in_child_node_ids?: (string | null)[] }
            let childIds = [...(parentData.in_child_node_ids || [])]
            childIds = childIds.map((id) => (id === nodeId ? null : id))
            while (childIds.length > 0 && childIds[childIds.length - 1] === null) {
              childIds.pop()
            }
            // Update ReactFlow state: remove the child and update parent's in_child_node_ids
            setNodes((nds) =>
              nds
                .filter((n) => n.id !== nodeId)
                .map((n) =>
                  n.id === removedNode.parentNode
                    ? { ...n, data: { ...n.data, in_child_node_ids: childIds } }
                    : n,
                ),
            )
            // Persist to YDoc
            writeNodeData(removedNode.parentNode, { in_child_node_ids: childIds })
            // Remove edges connected to this node
            setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId))
            // Backend removal
            if (projectId) {
              removeNodesFromGraph(projectId, [nodeId]).catch((error) => {
                console.error('[WorkspaceV2:removeNode] Backend removal failed:', error)
              })
            }
            return
          }
        }
        // Original logic for non-group-child nodes
        setNodes((nds) => nds.filter((n) => n.id !== nodeId))
        setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId))
        if (projectId) {
          removeNodesFromGraph(projectId, [nodeId]).catch((error) => {
            console.error('[WorkspaceV2:removeNode] Backend removal failed:', error)
          })
        }
      },

      removeEdge: (edgeId: string) => {
        setEdges((eds) => eds.filter((e) => e.id !== edgeId))
      },

      duplicateNode: (nodeId: string) => {
        const sourceNode = nodesRef.current.find((n) => n.id === nodeId)
        if (!sourceNode) return undefined

        const newId = `${sourceNode.type}-${Date.now()}`
        const clonedData = structuredClone(sourceNode.data)
        const newNode: Node = {
          ...sourceNode,
          id: newId,
          position: {
            x: sourceNode.position.x + PASTE_OFFSET,
            y: sourceNode.position.y + PASTE_OFFSET,
          },
          selected: true,
          data: {
            ...clonedData,
            projectId,
            status: 'idle',
            progress: undefined,
            progressMessage: undefined,
            error: undefined,
          },
        }

        // Re-target incoming edges
        const incomingEdges = edgesRef.current.filter((e) => e.target === nodeId)
        const newEdges: Edge[] = incomingEdges.map((edge, i) => ({
          ...edge,
          id: `dup-edge-${Date.now()}-${i}`,
          target: newId,
        }))

        setNodes((nds) => {
          const updated = [...nds.map((n) => ({ ...n, selected: false })), newNode]
          nodesRef.current = updated
          return updated
        })
        if (newEdges.length > 0) {
          setEdges((eds) => {
            const updated = [...eds, ...newEdges]
            edgesRef.current = updated
            return updated
          })
        }

        // Sync to Y doc + backend
        if (shouldSyncToBackend(sourceNode.type ?? '')) {
          addNodeToYDoc(
            newId,
            sourceNode.type ?? '',
            (newNode.data as Record<string, unknown>)?.label as string ??
              (newNode.data as Record<string, unknown>)?.name as string ??
              newId,
            newNode.position.x,
            newNode.position.y,
            newNode.data as Record<string, unknown>,
            true
          )
        }
        if (projectId && shouldSyncToBackend(sourceNode.type ?? '')) {
          const backendType =
            FRONTEND_TO_BACKEND_TYPE[sourceNode.type as keyof typeof FRONTEND_TO_BACKEND_TYPE]
          addNodesToGraph(projectId, [
            { id: newId, type: backendType, config: newNode.data as Record<string, unknown> },
          ]).catch((error) => {
            console.error('[WorkspaceV2:duplicateNode] Backend sync failed:', error)
          })
          if (newEdges.length > 0) {
            addEdgesToGraph(
              projectId,
              newEdges.map((e) => ({
                from: e.source,
                to: e.target,
                fromPort: e.sourceHandle || undefined,
                toPort: e.targetHandle || undefined,
              }))
            ).catch((error) => {
              console.error('[WorkspaceV2:duplicateNode] Backend edge sync failed:', error)
            })
          }
        }

        return newId
      },

      updateNodeData: (nodeId: string, updates: Record<string, unknown>) => {
        // 1. Immediate ReactFlow state update
        setNodes((nds) =>
          nds.map((n) =>
            n.id === nodeId ? { ...n, data: { ...n.data, ...updates } } : n
          )
        )

        // 2. Immediate Y doc write (no-op for nodes not in Y doc)
        // YDoc is the sole persistence path for in_*/out_* fields (RFC-0328).
        writeNodeData(nodeId, updates)
      },

      persistNodeData: writeNodeData,

      updateNodeParent: (nodeId: string, parentId: string | null) => {
        writeNodeParent(nodeId, parentId)
      },

      executeNode: (nodeId: string) => {
        const node = nodesRef.current.find((n) => n.id === nodeId)
        if (node) {
          handleExecuteNode(node)
        } else {
          console.warn(`[WorkspaceV2] executeNode: Node not found: ${nodeId}`)
        }
      },

      getNode: (nodeId: string) => nodesRef.current.find((n) => n.id === nodeId),
      getNodes: () => nodesRef.current,
      updateNodePosition: writeNodePosition,
    }),
    [projectId, project?.updated_at, setNodes, setEdges, handleExecuteNode, addNodeToYDoc, writeNodeData, writeNodeParent, writeNodePosition]
  )

  // ─── Undo/Redo ────────────────────────────────────────────────────────────

  const recordForUndo = useCallback(() => {
    if (isUndoRedo.current) return
    undoStack.current.push({ nodes: [...nodesRef.current], edges: [...edgesRef.current] })
    if (undoStack.current.length > MAX_UNDO) undoStack.current.shift()
    redoStack.current = []
  }, [])

  const syncStateDiffToBackend = useCallback(
    (oldNodes: Node[], oldEdges: Edge[], newNodes: Node[], newEdges: Edge[]) => {
      if (!projectId) return

      const oldNodeIds = new Set(oldNodes.map((n) => n.id))
      const newNodeIds = new Set(newNodes.map((n) => n.id))

      // Restored nodes
      const restoredNodes = newNodes.filter((n) => !oldNodeIds.has(n.id))
      if (restoredNodes.length > 0) {
        for (const n of restoredNodes) {
          if (shouldSyncToBackend(n.type ?? '')) {
            addNodeToYDoc(
              n.id,
              n.type ?? '',
              (n.data as Record<string, unknown>)?.label as string ??
                (n.data as Record<string, unknown>)?.name as string ??
                n.id,
              n.position.x,
              n.position.y,
              n.data as Record<string, unknown>,
              true
            )
          }
        }
        const toAdd = restoredNodes
          .filter((n) => shouldSyncToBackend(n.type ?? ''))
          .map((n) => ({
            id: n.id,
            type: FRONTEND_TO_BACKEND_TYPE[n.type as keyof typeof FRONTEND_TO_BACKEND_TYPE] || n.type || '',
            config: n.data as Record<string, unknown>,
          }))
        if (toAdd.length > 0) {
          addNodesToGraph(projectId, toAdd).catch((err) =>
            console.error('[WorkspaceV2:syncDiff] addNodesToGraph FAILED:', err)
          )
        }
      }

      // Removed nodes
      const removedNodeIds = oldNodes.filter((n) => !newNodeIds.has(n.id)).map((n) => n.id)
      if (removedNodeIds.length > 0) {
        removeNodesFromGraph(projectId, removedNodeIds).catch((err) =>
          console.error('[WorkspaceV2:syncDiff] removeNodesFromGraph FAILED:', err)
        )
      }

      const oldEdgeIds = new Set(oldEdges.map((e) => e.id))
      const newEdgeIds = new Set(newEdges.map((e) => e.id))

      // Restored edges
      const restoredEdges = newEdges.filter((e) => !oldEdgeIds.has(e.id))
      if (restoredEdges.length > 0) {
        addEdgesToGraph(
          projectId,
          restoredEdges.map((e) => ({
            from: e.source,
            to: e.target,
            fromPort: e.sourceHandle || undefined,
            toPort: e.targetHandle || undefined,
          }))
        ).catch((err) =>
          console.error('[WorkspaceV2:syncDiff] addEdgesToGraph FAILED:', err)
        )
      }

      // Removed edges
      const removedEdges = oldEdges.filter((e) => !newEdgeIds.has(e.id))
      if (removedEdges.length > 0) {
        removeEdgesFromGraph(
          projectId,
          removedEdges.map((e) => ({
            from: e.source,
            to: e.target,
            fromPort: e.sourceHandle || undefined,
            toPort: e.targetHandle || undefined,
          }))
        ).catch((err) =>
          console.error('[WorkspaceV2:syncDiff] removeEdgesFromGraph FAILED:', err)
        )
      }

      // Position changes
      const oldPositions = new Map(oldNodes.map((n) => [n.id, n.position]))
      for (const n of newNodes) {
        const oldPos = oldPositions.get(n.id)
        if (
          oldPos &&
          (Math.abs(oldPos.x - n.position.x) > 0.5 || Math.abs(oldPos.y - n.position.y) > 0.5)
        ) {
          writeNodePosition(n.id, n.position.x, n.position.y)
        }
      }
    },
    [projectId, addNodeToYDoc, writeNodePosition]
  )

  const handleUndo = useCallback(() => {
    if (undoStack.current.length === 0) return
    const currentNodes = [...nodesRef.current]
    const currentEdges = [...edgesRef.current]
    redoStack.current.push({ nodes: currentNodes, edges: currentEdges })

    const prevState = undoStack.current.pop()!
    isUndoRedo.current = true
    nodesRef.current = prevState.nodes
    edgesRef.current = prevState.edges
    setNodes(prevState.nodes)
    setEdges(prevState.edges)

    syncStateDiffToBackend(currentNodes, currentEdges, prevState.nodes, prevState.edges)
    setTimeout(() => { isUndoRedo.current = false }, 500)
  }, [setNodes, setEdges, syncStateDiffToBackend])

  const handleRedo = useCallback(() => {
    if (redoStack.current.length === 0) return
    const currentNodes = [...nodesRef.current]
    const currentEdges = [...edgesRef.current]
    undoStack.current.push({ nodes: currentNodes, edges: currentEdges })

    const nextState = redoStack.current.pop()!
    isUndoRedo.current = true
    nodesRef.current = nextState.nodes
    edgesRef.current = nextState.edges
    setNodes(nextState.nodes)
    setEdges(nextState.edges)

    syncStateDiffToBackend(currentNodes, currentEdges, nextState.nodes, nextState.edges)
    setTimeout(() => { isUndoRedo.current = false }, 500)
  }, [setNodes, setEdges, syncStateDiffToBackend])

  // ─── Copy / Paste / Duplicate ─────────────────────────────────────────────

  const [clipboard, setClipboard] = useState<{ nodes: Node[]; edges: Edge[] }>({
    nodes: [],
    edges: [],
  })

  const handleCopy = useCallback(() => {
    const selectedNodes = nodesRef.current.filter((n) => n.selected)
    if (selectedNodes.length === 0) return
    const selectedIds = new Set(selectedNodes.map((n) => n.id))
    const relevantEdges = edgesRef.current.filter(
      (e) => selectedIds.has(e.target) || (selectedIds.has(e.source) && selectedIds.has(e.target))
    )
    setClipboard({ nodes: selectedNodes, edges: relevantEdges })
  }, [])

  const handlePaste = useCallback(() => {
    if (clipboard.nodes.length === 0) return
    recordForUndo()

    const oldToNew = new Map<string, string>()
    const newNodes: Node[] = clipboard.nodes.map((node, index) => {
      const newId = `${node.type}-${Date.now()}-${index}`
      oldToNew.set(node.id, newId)
      return {
        ...node,
        id: newId,
        position: {
          x: node.position.x + PASTE_OFFSET,
          y: node.position.y + PASTE_OFFSET,
        },
        selected: true,
        data: {
          ...structuredClone(node.data),
          projectId,
          status: 'idle',
          progress: undefined,
          progressMessage: undefined,
          error: undefined,
        },
      }
    })

    const newEdges: Edge[] = clipboard.edges.map((edge, index) => ({
      ...edge,
      id: `paste-edge-${Date.now()}-${index}`,
      source: oldToNew.get(edge.source) ?? edge.source,
      target: oldToNew.get(edge.target)!,
    }))

    setNodes((nds) => {
      const updated = [...nds.map((n) => ({ ...n, selected: false })), ...newNodes]
      nodesRef.current = updated
      return updated
    })
    if (newEdges.length > 0) {
      setEdges((eds) => {
        const updated = [...eds, ...newEdges]
        edgesRef.current = updated
        return updated
      })
    }

    // Sync to Y doc + backend
    for (const n of newNodes) {
      if (shouldSyncToBackend(n.type ?? '')) {
        addNodeToYDoc(
          n.id,
          n.type ?? '',
          (n.data as Record<string, unknown>)?.label as string ??
            (n.data as Record<string, unknown>)?.name as string ??
            n.id,
          n.position.x,
          n.position.y,
          n.data as Record<string, unknown>,
          true
        )
      }
    }
    if (projectId) {
      for (const n of newNodes) {
        if (shouldSyncToBackend(n.type ?? '')) {
          const backendType = FRONTEND_TO_BACKEND_TYPE[n.type as keyof typeof FRONTEND_TO_BACKEND_TYPE]
          addNodesToGraph(projectId, [
            { id: n.id, type: backendType, config: n.data as Record<string, unknown> },
          ]).catch((error) => {
            console.error('[WorkspaceV2:paste] Backend sync failed:', error)
          })
        }
      }
      if (newEdges.length > 0) {
        addEdgesToGraph(
          projectId,
          newEdges.map((e) => ({
            from: e.source,
            to: e.target,
            fromPort: e.sourceHandle || undefined,
            toPort: e.targetHandle || undefined,
          }))
        ).catch((error) => {
          console.error('[WorkspaceV2:paste] Backend edge sync failed:', error)
        })
      }
    }

    // Update clipboard for sequential pastes
    setClipboard({ nodes: newNodes, edges: newEdges })
  }, [clipboard, setNodes, setEdges, recordForUndo, projectId, addNodeToYDoc])

  const handleDuplicate = useCallback(() => {
    const selectedNodes = nodesRef.current.filter((n) => n.selected)
    if (selectedNodes.length === 0) return
    recordForUndo()

    const selectedIds = new Set(selectedNodes.map((n) => n.id))
    const relevantEdges = edgesRef.current.filter(
      (e) => selectedIds.has(e.target) || (selectedIds.has(e.source) && selectedIds.has(e.target))
    )

    const oldToNew = new Map<string, string>()
    const newNodes: Node[] = selectedNodes.map((node, index) => {
      const newId = `${node.type}-${Date.now()}-${index}`
      oldToNew.set(node.id, newId)
      return {
        ...node,
        id: newId,
        position: {
          x: node.position.x + PASTE_OFFSET,
          y: node.position.y + PASTE_OFFSET,
        },
        selected: true,
        data: {
          ...structuredClone(node.data),
          projectId,
          status: 'idle',
          progress: undefined,
          progressMessage: undefined,
          error: undefined,
        },
      }
    })

    const newEdges: Edge[] = relevantEdges.map((edge, index) => ({
      ...edge,
      id: `dup-edge-${Date.now()}-${index}`,
      source: oldToNew.get(edge.source) ?? edge.source,
      target: oldToNew.get(edge.target)!,
    }))

    setNodes((nds) => {
      const updated = [...nds.map((n) => ({ ...n, selected: false })), ...newNodes]
      nodesRef.current = updated
      return updated
    })
    if (newEdges.length > 0) {
      setEdges((eds) => {
        const updated = [...eds, ...newEdges]
        edgesRef.current = updated
        return updated
      })
    }

    // Sync to Y doc + backend
    for (const n of newNodes) {
      if (shouldSyncToBackend(n.type ?? '')) {
        addNodeToYDoc(
          n.id,
          n.type ?? '',
          (n.data as Record<string, unknown>)?.label as string ??
            (n.data as Record<string, unknown>)?.name as string ??
            n.id,
          n.position.x,
          n.position.y,
          n.data as Record<string, unknown>,
          true
        )
      }
    }
    if (projectId) {
      for (const n of newNodes) {
        if (shouldSyncToBackend(n.type ?? '')) {
          const backendType = FRONTEND_TO_BACKEND_TYPE[n.type as keyof typeof FRONTEND_TO_BACKEND_TYPE]
          addNodesToGraph(projectId, [
            { id: n.id, type: backendType, config: n.data as Record<string, unknown> },
          ]).catch((error) => {
            console.error('[WorkspaceV2:duplicate] Backend sync failed:', error)
          })
        }
      }
      if (newEdges.length > 0) {
        addEdgesToGraph(
          projectId,
          newEdges.map((e) => ({
            from: e.source,
            to: e.target,
            fromPort: e.sourceHandle || undefined,
            toPort: e.targetHandle || undefined,
          }))
        ).catch((error) => {
          console.error('[WorkspaceV2:duplicate] Backend edge sync failed:', error)
        })
      }
    }
  }, [setNodes, setEdges, recordForUndo, projectId, addNodeToYDoc])

  // ─── onNodesChange (undo recording + dimension sync + backend removal) ────

  const onNodesChange = useCallback(
    (changes: Parameters<typeof onNodesChangeBase>[0]) => {
      // ─── Scalar selection: intercept selection events ────────────────
      // Instead of relying on ReactFlow's internal nodeInternals (which
      // lags one frame behind), we drive selection from a single scalar.
      // Strip ALL selection changes from the batch and stamp node.selected
      // ourselves.
      // Find the last selection/deselection event in the batch. We iterate
      // in reverse so that if ReactFlow sends multiple events (e.g. lasso),
      // "last click wins" — consistent with single-select semantics.
      let selectChange: NodeSelectionChange | undefined
      let deselectChange: NodeSelectionChange | undefined
      for (let i = changes.length - 1; i >= 0; i--) {
        const c = changes[i]
        if (c.type === 'select') {
          if (c.selected && !selectChange) {
            selectChange = c
          } else if (!c.selected && !deselectChange) {
            deselectChange = c
          }
        }
      }

      let selectionChanged = false
      if (selectChange) {
        selectedNodeIdRef.current = selectChange.id
        setSelectedNodeId(selectChange.id)
        selectionChanged = true
      } else if (deselectChange && deselectChange.id === selectedNodeIdRef.current) {
        // User clicked canvas background (deselect all)
        selectedNodeIdRef.current = null
        setSelectedNodeId(null)
        selectionChanged = true
      }

      // Strip ALL selection changes — we drive selection ourselves
      const nonSelectionChanges = changes.filter(c => c.type !== 'select')

      const hasRemoval = changes.some((c) => c.type === 'remove')
      if (hasRemoval && !isUndoRedo.current) {
        undoStack.current.push({ nodes: [...nodesRef.current], edges: [...edgesRef.current] })
        if (undoStack.current.length > MAX_UNDO) undoStack.current.shift()
        redoStack.current = []
      }

      // Clean up parent group's in_child_node_ids when a child node is removed
      if (hasRemoval) {
        const removedIds = changes
          .filter((c): c is { type: 'remove'; id: string } => c.type === 'remove')
          .map((c) => c.id)

        // Check each removed node for a parentNode (group membership)
        const currentNodes = nodesRef.current
        const parentUpdates = new Map<string, (string | null)[]>()

        for (const removedId of removedIds) {
          const removedNode = currentNodes.find((n) => n.id === removedId)
          if (!removedNode?.parentNode) continue

          const parentId = removedNode.parentNode
          const parentNode = currentNodes.find((n) => n.id === parentId)
          if (!parentNode || parentNode.type !== 'nodeGroup') continue

          // Get or initialize the child IDs for this parent
          const parentData = parentNode.data as { in_child_node_ids?: (string | null)[] }
          let childIds = parentUpdates.get(parentId) ?? [...(parentData.in_child_node_ids || [])]

          // Replace the removed child's ID with null (empty slot), then trim trailing nulls
          childIds = childIds.map((id) => (id === removedId ? null : id))
          while (childIds.length > 0 && childIds[childIds.length - 1] === null) {
            childIds.pop()
          }
          parentUpdates.set(parentId, childIds)
        }

        // Apply parent updates to ReactFlow state and YDoc
        if (parentUpdates.size > 0) {
          setNodes((nds) =>
            nds.map((n) => {
              const newChildIds = parentUpdates.get(n.id)
              if (newChildIds !== undefined) {
                return { ...n, data: { ...n.data, in_child_node_ids: newChildIds } }
              }
              return n
            }),
          )
          for (const [parentId, newChildIds] of parentUpdates) {
            writeNodeData(parentId, { in_child_node_ids: newChildIds })
          }
        }
      }

      onNodesChangeBase(nonSelectionChanges)

      // Stamp selection synchronously only when it actually changed.
      // This avoids wasted reconciliation on drag/resize events (which fire
      // onNodesChange with position/dimension changes but no selection changes).
      if (selectionChanged) {
        setNodes(prev => prev.map(n => ({
          ...n,
          selected: n.id === selectedNodeIdRef.current,
        })))
      }

      // Backend node removal
      if (hasRemoval && projectId) {
        const removedIds = nonSelectionChanges
          .filter((c): c is { type: 'remove'; id: string } => c.type === 'remove')
          .map((c) => c.id)
        if (removedIds.length > 0) {
          removeNodesFromGraph(projectId, removedIds).catch((error) => {
            console.error('[WorkspaceV2:onNodesChange] Backend removal failed:', error)
          })
        }
      }

      // Dimension sync
      for (const c of nonSelectionChanges) {
        if (c.type === 'dimensions' && c.dimensions) {
          dimSyncLog('[position] onNodesChange dimension: id=%s w=%d h=%d', c.id, c.dimensions.width, c.dimensions.height)
          writeNodeDimensions(c.id, c.dimensions.width, c.dimensions.height)
        }
      }
    },
    [onNodesChangeBase, projectId, writeNodeDimensions, setNodes, writeNodeData, setSelectedNodeId]
  )

  // ─── onSelectionChange: sync scalar from programmatic setNodes ──────
  // Catches any programmatic setNodes that modifies `selected` outside the
  // scalar (e.g. openPanelToTab, duplicateNode, handlePaste) and syncs the
  // ref back. Prevents feedback loops via identity check on the ref.
  const onSelectionChange = useCallback(({ nodes: selectedNodes }: { nodes: Node[] }) => {
    // Pick the last node if multiple are selected (e.g., handlePaste sets
    // selected: true on all pasted nodes). "Last wins" preserves single-select
    // semantics and gracefully collapses any multi-selection leaks.
    const selected = selectedNodes.length > 0 ? selectedNodes[selectedNodes.length - 1].id : null
    if (selected !== selectedNodeIdRef.current) {
      selectedNodeIdRef.current = selected
      setSelectedNodeId(selected)
    }
  }, [])

  // ─── onEdgesChange (undo recording + backend removal) ─────────────────────

  const onEdgesChange = useCallback(
    (changes: Parameters<typeof onEdgesChangeBase>[0]) => {
      const hasRemoval = changes.some((c) => c.type === 'remove')
      if (hasRemoval && !isUndoRedo.current) {
        undoStack.current.push({ nodes: [...nodesRef.current], edges: [...edgesRef.current] })
        if (undoStack.current.length > MAX_UNDO) undoStack.current.shift()
        redoStack.current = []
      }

      // Sync edge removals to backend
      if (projectId && hasRemoval) {
        const removedEdgeIds = changes
          .filter((c) => c.type === 'remove')
          .map((c) => c.id)
        const edgesToRemove = edgesRef.current
          .filter((e) => removedEdgeIds.includes(e.id))
          .map((e) => ({
            from: e.source,
            to: e.target,
            fromPort: e.sourceHandle || undefined,
            toPort: e.targetHandle || undefined,
          }))
        if (edgesToRemove.length > 0) {
          removeEdgesFromGraph(projectId, edgesToRemove).catch((error) => {
            console.error('[WorkspaceV2:onEdgesChange] Backend edge removal failed:', error)
          })
        }
      }

      onEdgesChangeBase(changes)
    },
    [onEdgesChangeBase, projectId]
  )

  // ─── onConnect ────────────────────────────────────────────────────────────

  const onConnect = useCallback(
    (params: Connection | Edge) => {
      // Record undo
      if (!isUndoRedo.current) {
        undoStack.current.push({ nodes: [...nodesRef.current], edges: [...edgesRef.current] })
        if (undoStack.current.length > MAX_UNDO) undoStack.current.shift()
        redoStack.current = []
      }

      // Check array ports
      const targetNode = nodesRef.current.find((n) => n.id === params.target)
      const targetSchema = targetNode
        ? nodeSchemas[targetNode.type as keyof typeof nodeSchemas]
        : null
      const targetPort = targetSchema?.inputs?.find(
        (i: { id: string }) => i.id === params.targetHandle
      )
      const isArrayPort = targetPort?.type?.endsWith('[]') || targetPort?.type === 'json'

      setEdges((eds) => {
        if (isArrayPort) {
          return rfAddEdge(params, eds)
        }
        const filtered = eds.filter(
          (e) => !(e.target === params.target && e.targetHandle === params.targetHandle)
        )
        return rfAddEdge(params, filtered)
      })

      // Backend sync
      if (projectId && params.source && params.target) {
        addEdgesToGraph(projectId, [
          {
            from: params.source,
            to: params.target,
            fromPort: params.sourceHandle || undefined,
            toPort: params.targetHandle || undefined,
          },
        ]).catch((error) => {
          console.error('[WorkspaceV2:onConnect] Backend edge sync failed:', error)
        })
      }
    },
    [setEdges, projectId]
  )

  // ─── Connection Validation ────────────────────────────────────────────────

  const isValidConnection = useCallback(
    (connection: Connection) => {
      const { source, target, sourceHandle, targetHandle } = connection
      if (!source || !target || !sourceHandle || !targetHandle) return false

      const sourceNode = nodesRef.current.find((n) => n.id === source)
      const targetNode = nodesRef.current.find((n) => n.id === target)
      if (!sourceNode || !targetNode) return false

      const sourceSchema = nodeSchemas[sourceNode.type as keyof typeof nodeSchemas]
      const targetSchema = nodeSchemas[targetNode.type as keyof typeof nodeSchemas]
      if (!sourceSchema || !targetSchema) return true

      const sourcePort = sourceSchema.outputs?.find(
        (o: { id: string }) => o.id === sourceHandle
      )
      const targetPort = targetSchema.inputs?.find(
        (i: { id: string }) => i.id === targetHandle
      )
      if (!sourcePort || !targetPort) return true

      return areTypesCompatible(sourcePort.type, targetPort.type)
    },
    []
  )

  // ─── Drag Handlers ────────────────────────────────────────────────────────

  const [isDragging, setIsDragging] = useState(false)

  const onNodeDragStart = useCallback(
    (_event: React.MouseEvent, _node: Node) => {
      setIsDragging(true)
      recordForUndo()
    },
    [recordForUndo]
  )

  const onNodeDragStop = useCallback(
    (_event: React.MouseEvent, node: Node, draggedNodes: Node[]) => {
      setIsDragging(false)
      const toWrite = draggedNodes.length > 0 ? draggedNodes : [node]
      for (const n of toWrite) {
        if (n.parentNode) continue
        dragLog('writePosition node=%s pos=(%d,%d) tainted=true',
          n.id, n.position.x, n.position.y)
        writeNodePosition(n.id, n.position.x, n.position.y, true)
      }
    },
    [writeNodePosition]
  )

  const onSelectionDragStop = useCallback(
    (_event: React.MouseEvent, nodes: Node[]) => {
      setIsDragging(false)
      for (const n of nodes) {
        if (n.parentNode) continue
        writeNodePosition(n.id, n.position.x, n.position.y, true)
      }
    },
    [writeNodePosition]
  )

  // Canvas-to-group drag handlers — pass canvas methods explicitly because
  // CanvasContext.Provider is rendered by THIS component, so useContext
  // inside the hook would read from an ancestor (null), not from ours.
  const canvasDragMethods = useMemo(() => ({
    updateNodeData: (nodeId: string, updates: Record<string, unknown>) => {
      writeNodeData(nodeId, updates)
    },
    updateNodeParent: writeNodeParent,
    updateNodePosition: writeNodePosition,
  }), [writeNodeData, writeNodeParent, writeNodePosition])

  const { onNodeDrag: onNodeDragOverGroup, onNodeDragStop: onNodeDragStopWithGroup } =
    useCanvasDragToGroup(onNodeDragStop, canvasDragMethods)

  // ─── Focus ReactFlow on node click ────────────────────────────────────────

  const onNodeClick = useCallback((_event: React.MouseEvent, _node: Node) => {
    const wrapper = document.querySelector('.react-flow') as HTMLElement | null
    if (wrapper && document.activeElement !== wrapper && !wrapper.contains(document.activeElement)) {
      wrapper.focus({ preventScroll: true })
    }
  }, [])

  // ─── Keyboard Shortcuts ───────────────────────────────────────────────────

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return
      }

      const isMod = e.metaKey || e.ctrlKey

      if (isMod && e.key === 'c') {
        const selection = window.getSelection()
        if (selection && selection.toString().length > 0) return
        e.preventDefault()
        handleCopy()
      } else if (isMod && e.key === 'v') {
        e.preventDefault()
        handlePaste()
      } else if (isMod && e.key === 'd') {
        e.preventDefault()
        handleDuplicate()
      } else if (isMod && e.key === 'z' && !e.shiftKey) {
        e.preventDefault()
        handleUndo()
      } else if (isMod && e.key === 'z' && e.shiftKey) {
        e.preventDefault()
        handleRedo()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleCopy, handlePaste, handleDuplicate, handleUndo, handleRedo])

  // ─── Trackpad Pinch-to-Zoom ───────────────────────────────────────────────

  useEffect(() => {
    let canvasEl: Element | null = null
    const ZOOM_SENSITIVITY = 0.01
    const MIN_ZOOM = 0.1
    const MAX_ZOOM = 4

    const handler = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return
      if (!canvasEl) canvasEl = document.querySelector('.react-flow')
      if (!canvasEl?.contains(e.target as globalThis.Node)) return

      e.preventDefault()

      const target = e.target as HTMLElement
      if (!target.closest('.nowheel')) return

      const rf = reactFlowInstance.current
      if (!rf) return

      const rect = canvasEl.getBoundingClientRect()
      const mouseX = e.clientX - rect.left
      const mouseY = e.clientY - rect.top

      const vp = rf.getViewport()
      const zoomFactor = 1 - e.deltaY * ZOOM_SENSITIVITY
      const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, vp.zoom * zoomFactor))
      const ratio = newZoom / vp.zoom

      rf.setViewport(
        {
          x: mouseX - (mouseX - vp.x) * ratio,
          y: mouseY - (mouseY - vp.y) * ratio,
          zoom: newZoom,
        },
        { duration: 0 }
      )
    }

    document.addEventListener('wheel', handler, { passive: false, capture: true })
    return () => document.removeEventListener('wheel', handler, { capture: true })
  }, [])

  // ─── Shift Key Tracking ───────────────────────────────────────────────────

  useEffect(() => {
    const down = (e: KeyboardEvent) => { if (e.key === 'Shift') shiftHeldRef.current = true }
    const up = (e: KeyboardEvent) => { if (e.key === 'Shift') shiftHeldRef.current = false }
    window.addEventListener('keydown', down)
    window.addEventListener('keyup', up)
    return () => {
      window.removeEventListener('keydown', down)
      window.removeEventListener('keyup', up)
    }
  }, [])
  const shiftHeldRef = useRef(false)

  // ─── handleAddNode (from toolbox) ─────────────────────────────────────────

  const handleAddNode = useCallback(
    (node: ReturnType<typeof createNode>) => {
      const isGroupNode = node.type === 'nodeGroup'

      const enrichedNode = {
        ...node,
        data: { ...node.data, projectId },
        // Group nodes need transparent background and no border so the
        // ReactFlow wrapper doesn't show through as a gray rectangle.
        ...(isGroupNode ? {
          style: {
            width: 355,
            padding: 0,
            backgroundColor: 'transparent',
            border: 'none',
          },
        } : {}),
      } as unknown as Node

      setNodes((nds) => {
        const updated = [...nds, enrichedNode]
        nodesRef.current = updated
        return updated
      })

      if (node.position && shouldSyncToBackend(node.type ?? '')) {
        addNodeToYDoc(
          node.id,
          node.type ?? '',
          (node.data as Record<string, unknown>)?.label as string ??
            (node.data as Record<string, unknown>)?.name as string ??
            node.id,
          node.position.x,
          node.position.y,
          node.data as Record<string, unknown>,
          true
        )
      }

      // Group nodes are frontend-only but need a Y doc entry so writeNodeData
      // can persist child_node_ids and other data updates.
      if (node.type === 'group' && node.position) {
        addNodeToYDoc(
          node.id,
          'group',
          (node.data as Record<string, unknown>)?.label as string ?? 'Group',
          node.position.x,
          node.position.y,
          node.data as Record<string, unknown>,
        )
      }

      if (projectId && shouldSyncToBackend(node.type ?? '')) {
        const backendType = FRONTEND_TO_BACKEND_TYPE[node.type as keyof typeof FRONTEND_TO_BACKEND_TYPE]
        addNodesToGraph(projectId, [
          { id: node.id, type: backendType, config: node.data as Record<string, unknown> },
        ])
          .then(() => {
            setNodes((nds) =>
              nds.map((n) =>
                n.id === node.id ? { ...n, data: { ...n.data, syncedToBackend: true } } : n
              )
            )
          })
          .catch((error) => {
            console.error('[WorkspaceV2:handleAddNode] Backend sync failed:', error)
          })
      }
    },
    [setNodes, projectId, addNodeToYDoc]
  )

  // ─── Director Upload → Node Creation ──────────────────────────────────────

  const handleUploadFromDirector = useCallback(
    (file: File, type: 'image' | 'video' | 'audio') => {
      const rf = reactFlowInstance.current
      if (!rf) return

      // Map type to node type (identity — type names are the same)
      const nodeType = type
      if (!nodeType) return

      // Calculate viewport center position
      const viewport = rf.getViewport()
      const canvasEl = document.querySelector('.react-flow')
      const width = canvasEl?.clientWidth ?? window.innerWidth
      const height = canvasEl?.clientHeight ?? window.innerHeight
      const centerScreen = { x: width / 2, y: height / 2 }
      const flowPos = rf.screenToFlowPosition(centerScreen)

      // Simple offset to avoid overlapping — check existing nodes near center
      const OFFSET_STEP = 40
      let offsetX = 0
      let offsetY = 0
      const existingPositions = nodesRef.current.map(n => n.position)
      for (let i = 0; i < 10; i++) {
        const testX = flowPos.x + offsetX
        const testY = flowPos.y + offsetY
        const tooClose = existingPositions.some(
          p => Math.abs(p.x - testX) < 50 && Math.abs(p.y - testY) < 50
        )
        if (!tooClose) break
        offsetX += OFFSET_STEP
        offsetY += OFFSET_STEP
      }

      const position = { x: flowPos.x + offsetX, y: flowPos.y + offsetY }

      // Create blob URL eagerly so the first render shows the preview + spinner
      // instead of the empty "Upload Image" placeholder.
      const previewUrl = URL.createObjectURL(file)

      // Create the node with initialFile, preview URL, and running status
      const newNode = createNode(nodeType, position, {
        initialFile: file,
        _initialPreviewUrl: previewUrl,
        status: 'running',
        in_file_name: file.name,
        projectId,
      })

      handleAddNode(newNode)

      // Pan to the new node without changing zoom
      const currentZoom = viewport.zoom
      requestAnimationFrame(() => {
        rf.fitView({
          nodes: [{ id: newNode.id, position, data: {} } as Node],
          padding: 0.3,
          duration: 500,
          maxZoom: currentZoom,
        })
      })
    },
    [projectId, handleAddNode]
  )

  // ─── Canvas-Level Drag-and-Drop ───────────────────────────────────────────

  const handleCanvasDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
  }, [])

  const handleCanvasDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      const rf = reactFlowInstance.current
      if (!rf) return

      const files = e.dataTransfer.files
      if (files.length === 0) return

      const file = files[0]
      let nodeType: string | null = null
      if (file.type.startsWith('image/')) nodeType = 'image'
      else if (file.type.startsWith('video/')) nodeType = 'video'
      else if (file.type.startsWith('audio/')) nodeType = 'audio'

      // Ignore unsupported MIME types
      if (!nodeType) return

      // Convert drop position to flow coordinates
      const position = rf.screenToFlowPosition({ x: e.clientX, y: e.clientY })

      // Create upload node at drop position with initialFile and eager preview
      const previewUrl = URL.createObjectURL(file)
      const newNode = createNode(nodeType, position, {
        initialFile: file,
        _initialPreviewUrl: previewUrl,
        status: 'running',
        in_file_name: file.name,
        projectId,
      })

      handleAddNode(newNode)
    },
    [projectId, handleAddNode]
  )

  // ─── Loading / Error States ───────────────────────────────────────────────

  if (isLoading) {
    return <LoadingState />
  }

  if (!project) {
    return <ErrorState />
  }

  // ─── Render ───────────────────────────────────────────────────────────────


  return (
    <ProjectIdProvider value={projectId}>
    <CanvasContext.Provider value={canvasContextValue}>
      <NodeHoverContext.Provider value={nodeHoverState}>
        <div data-testid="workspace-v2" className="fixed inset-0 flex bg-background">
          {/* Chat Sidebar (Left) - Collapsible & Resizable */}
          {projectId && (
            <ChatSidebar
              projectId={projectId}
              projectName={project?.name}
              initialPrompt={initialPrompt}
              style={initialStyle}
              productionTarget={initialProductionTarget}
              onStop={handleStop}
              skipCostConfirmation={skipCostConfirmation}
              onSkipCostChange={setSkipCostConfirmation}
              canvasNodes={canvasNodesStable}
              ydocRunningCount={ydocRunningCount}
              ydocQueuedCount={ydocQueuedCount}
              onMentionClick={handleMentionClick}
              onUploadFile={handleUploadFromDirector}
            />
          )}

          {/* Canvas */}
          <div className="flex-1 relative">
            <div className="absolute inset-0">
              <ReactFlowCapture instanceRef={reactFlowInstance} onReady={onRfInstanceReady} />
              <ReactFlow
                style={{ opacity: canvasVisible ? 1 : 0 }}
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onMoveEnd={onMoveEndSave}
                onNodeClick={onNodeClick}
                onNodeDragStart={onNodeDragStart}
                onNodeDrag={onNodeDragOverGroup}
                onNodeDragStop={onNodeDragStopWithGroup}
                onSelectionDragStop={onSelectionDragStop}
                onSelectionChange={onSelectionChange}
                onDragOver={handleCanvasDragOver}
                onDrop={handleCanvasDrop}
                isValidConnection={isValidConnection}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                deleteKeyCode={['Backspace', 'Delete']}
                defaultViewport={{ x: 0, y: 0, zoom: 1 }}
                minZoom={0.1}
                maxZoom={4}
                panOnScroll
                panOnScrollMode={PanOnScrollMode.Free}
                panOnDrag
                zoomOnScroll={false}
                zoomOnPinch
                zoomOnDoubleClick
                snapToGrid={!isDragging}
                snapGrid={[20, 20]}
                className="bg-background canvas-gradient-bg"
                proOptions={{ hideAttribution: true }}
              >
                <Background
                  variant={BackgroundVariant.Dots}
                  gap={24}
                  size={1}
                  color="rgba(255,255,255,0.08)"
                />


                {/* Canvas Controls (bottom-left zoom controls) */}
                <CanvasControls />

                {/* Interactive MiniMap */}
                <InteractiveMiniMap />

                {/* Canvas Toolbox - center right (rendered BEFORE sparks so it's below in z-order) */}
                <Panel
                  position="top-right"
                  className="mr-4 !top-0 !bottom-0 flex items-center pointer-events-none"
                >
                  <div className="pointer-events-auto">
                    <CanvasToolbox onAddNode={handleAddNode} onUploadFile={handleUploadFromDirector} />
                  </div>
                </Panel>

                {/* Top-right: Share + Sparks balance (rendered AFTER toolbox so it's on top) */}
                <Panel position="top-right" className="mr-4 mt-4">
                  <div className="flex items-center gap-2">
                    {projectId && <PublishButton projectId={projectId} />}
                    <SparksBalance onClick={() => setShowUsageCost(true)} />
                  </div>
                </Panel>
              </ReactFlow>
            </div>
          </div>
        </div>

        <UsageCostModal
          isOpen={showUsageCost}
          onClose={() => {
            setShowUsageCost(false)
          }}
          projectId={projectId}
        />
      </NodeHoverContext.Provider>
    </CanvasContext.Provider>
    </ProjectIdProvider>
  )
}

// ─── Export ───────────────────────────────────────────────────────────────────

export default function WorkspacePageV2() {
  return (
    <ReactFlowProvider>
      <GroupDropTargetProvider>
        <WorkspaceV2Inner />
      </GroupDropTargetProvider>
    </ReactFlowProvider>
  )
}
